// Report-param grammar for the conjoint blocks, ported from the coffee block
// (importReportParams / updateReportParams / the sort, uncollapsed and market-simulator pairs)
// and Conjoint.Lib.serializeParams / deserializeParams.
//
// The params carry the block's state, addressed by prefix:
//   cjc  the packed visual state (package ! hidden batches ! center ! scale ! mode)
//   cjt  the summary-chart view
//   cjd  the selected cluster persona (segmentation only)
//   cjv  the pinned data slice - lite or full (only written when the researcher asks for full)
//   cm   the lite chart's mode
//   z    the lite chart's zoom (shared with the other views of the question)
//   s    the sort order (shared with the other views of the question)
//   cb   which batches are expanded
//   msp / msna  the market simulator's packages and its no-answer option

import {
  DEFAULT_SORT_ORDER,
  decodeSortParam,
  encodeSortParam
} from '@aytm/app/stats/page/question-views/components/lib/sort-param'
import { decodeZoomParam, encodeZoomParam } from '@aytm/app/stats/page/question-views/components/lib/zoom-param'

export const DEFAULT_MODE = 'exp'
export { DEFAULT_SORT_ORDER }
export const SUMMARY_CHART_VIEW = 'summary-chart'

// The packed cjc value with everything at its default; nothing to store.
const EMPTY_SECTION = '!!!s!exp'

// center and scale are pinned: the block stopped offering them, but their slots stay so an
// older stored value still parses positionally.
export const serializeParams = ({ isMedian, currentPackage = [], hiddenBatches = [], mode } = {}) => {
  const cp = isMedian ? '' : currentPackage.join('.')

  return `${cp}!${hiddenBatches.join('.')}!!s!${mode ?? DEFAULT_MODE}`
}

export const deserializeParams = serialized => {
  const parts = serialized?.split('!')

  return {
    currentPackage: parts?.[0]?.length ? parts[0] : '',
    hiddenBatches: parts?.[1]?.length ? parts[1].split('.').map(value => parseInt(value, 10)) : [],
    center: false,
    scale: true,
    mode: parts?.[4] ? parts[4] : DEFAULT_MODE
  }
}

export const resolveSortOrder = (section, defaultSortOrder = DEFAULT_SORT_ORDER) =>
  decodeSortParam(section, { defaultSortOrder, custom: false })

const resolveMarketSimulator = ({ msp, msna, answer }) => ({
  // May run before the answer is in, so the stored value wins over what the answer offers.
  marketSimulatorNA: null != msna ? Boolean(parseInt(msna, 10)) : Boolean(answer?.hasNA?.[0]),
  marketSimulatorPackages: msp
    ? String(msp)
        .split('!')
        .map(entry => entry.split('.').map(value => parseInt(value, 10)))
    : []
})

// A stored package that no longer matches the question falls back to the median one.
export const resolveCurrentPackage = ({ stored, mainPackages = [], isKnownPackage }) => {
  if (!stored.length || isKnownPackage?.(stored)) return stored

  return mainPackages.length ? mainPackages[2].join('.') : ''
}

export const resolveReportParams = ({
  params = {},
  answer,
  defaultSortOrder,
  mainPackages,
  isKnownPackage,
  isKnownPersona
} = {}) => {
  const packed = deserializeParams(params.cjc)
  const persona = parseInt(params.cjd || '0', 10) || 0

  return {
    ...packed,
    currentPackage: resolveCurrentPackage({ stored: packed.currentPackage, mainPackages, isKnownPackage }),
    cjtView: SUMMARY_CHART_VIEW === params.cjt ? SUMMARY_CHART_VIEW : null,
    cjView: 'lite' === params.cjv || 'full' === params.cjv ? params.cjv : null,
    persona: isKnownPersona?.(persona) ? persona : 0,
    chartMode: params.cm || null,
    zoomLevel: decodeZoomParam(params.z),
    sortOrder: resolveSortOrder(params.s, defaultSortOrder),
    uncollapsed: params.cb || '',
    ...resolveMarketSimulator({ msp: params.msp, msna: params.msna, answer })
  }
}

// The whole param set the block owns, keyed by prefix; a null clears the param. cjv is left out
// unless the researcher pinned a slice, so the size-derived default keeps deciding.
export const calcReportParamsSection = (
  state = {},
  { mainPackages = [], answer, questionType, defaultChartMode, globalZoomLevel } = {}
) => {
  const stored = state.currentPackage || ''
  const currentPackage = stored.length ? stored.split('.').map(value => parseInt(value, 10)) : []

  const medianPackage = mainPackages[2]
  const isMedian = Boolean(medianPackage) && currentPackage.every((value, i) => value === medianPackage[i])

  const packed = serializeParams({
    isMedian,
    currentPackage,
    hiddenBatches: state.hiddenBatches,
    mode: state.mode
  })

  const marketSimulatorPackages = (state.marketSimulatorPackages || []).map(entry => entry.join('.')).join('!')
  const uncollapsed = state.uncollapsed || ''

  return {
    cjc: EMPTY_SECTION === packed ? null : packed,
    cjt: SUMMARY_CHART_VIEW === state.cjtView ? SUMMARY_CHART_VIEW : null,
    cm: state.chartMode && state.chartMode !== defaultChartMode ? state.chartMode : null,
    z: encodeZoomParam(state.zoomLevel, globalZoomLevel),
    s: encodeSortParam(state.sortOrder, { custom: false }),
    cb: uncollapsed.length ? uncollapsed : null,
    msp: marketSimulatorPackages.length ? marketSimulatorPackages : null,
    // the no-answer option is only offered by a question whose answer carries it
    ...(answer?.hasNA?.[0] ? { msna: state.marketSimulatorNA ? null : 0 } : {}),
    // clusters, and so a persona to pick, only exist on the segmentation question
    ...('cjd' === questionType ? { cjd: state.persona || null } : {}),
    ...(state.cjView ? { cjv: state.cjView } : {})
  }
}

// The state the pptx / summary exporters read off the block.
export const calcExportData = (state = {}) => ({
  currentPackage: state.currentPackage || '',
  cjtView: state.cjtView,
  mode: state.mode || DEFAULT_MODE,
  hiddenBatches: state.hiddenBatches || [],
  sortOrder: state.sortOrder || DEFAULT_SORT_ORDER,
  persona: state.persona || 0
})
